import { useState, useEffect, useRef } from 'react';
import './searchBar.css'; 
import autocomplete from '../../api/autocompleteTrigram';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Loader from '../Loader';
import addUserContentRec from '../../api/addUserContentRec';

const SearchBar = ({ select, type, guessStatus, flip, usedIds, triggerHint }) => {
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [notFoundAdded, setNotFoundAdded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showNudgeEffect, setShowNudgeEffect] = useState(false);
    const [showTypeFlash, setShowTypeFlash] = useState(false);
    const prevTypeRef = useRef(type);
    const searchRef = useRef(null);

    // simple in-memory cache to avoid repeated network calls for the same query+type
    const cacheRef = useRef(new Map());

    useEffect(() => {
        if (prevTypeRef.current !== type) {
            prevTypeRef.current = type;
            setShowTypeFlash(true);
            setTimeout(() => setShowTypeFlash(false), 500);
        }
    }, [type]);

    useEffect(() => {
        if (query.length > 0) {
            return;
        }
        const timeoutId = setTimeout(() => {
            nudgeUser();
        }, 15000);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [query]);

    useEffect(() => {
        const minChars = 2;      // keep or increase to 3 if you want fewer requests
        const debounceMs = 300;  // previously 3000ms, much snappier now

        if (query.length < minChars) {
            setSuggestions([]);
            setLoading(false);
            return;
        }

        const cacheKey = `${type}:${query}`;
        const cached = cacheRef.current.get(cacheKey);
        if (cached) {
            setSuggestions(cached);
            setLoading(false);
            return;
        }

        setLoading(true);
        const abortController = new AbortController();

        const timeoutId = setTimeout(async () => {
            try {
                const results = await autocomplete(query, type, { signal: abortController.signal });
                cacheRef.current.set(cacheKey, results);
                setSuggestions(results);
            } catch (error) {
                if (error.name !== "AbortError") console.error(error);
            } finally {
                setLoading(false);
            }
        }, debounceMs);

        return () => {
            clearTimeout(timeoutId);
            abortController.abort();
            setLoading(false);
        };
    }, [query, type]);

    const handleInputChange = (e) => {
        setQuery(e.target.value);
        setNotFoundAdded(false);
        setShowNudgeEffect(false);
    };

    const nudgeUser = () => {
        if (searchRef.current) {
            setShowNudgeEffect(true);
            setTimeout(() => {
                setShowNudgeEffect(false);
            }, 1500);
        }
    };

    const handleSuggestionClick = (suggestion) => {
        select({ ...suggestion, type: type.toUpperCase() });
        setTimeout(() => {
            setQuery('');
            setSuggestions([]);
            focusInput();
        }, 1000);
    };

    const setGuessStatus = (id) => {
        if (!guessStatus){
            return 'black'; 
        } 
        else{
            if(guessStatus.id === id){
                let color = guessStatus.success ? 'green' : 'red';
                return color;
            }
            else{
                return 'black';
            }
        }
    };

    const showNotFoundMsg = () => {
        if (suggestions.length === 0 && query.length > 3) {
            const msg = notFoundAdded
                ? "Thanks for your help!"
                : `We couldn't find that ${type !== "MEDIA" ? 'actor or actress' : 'movie'} in our database. Want to add them?`;

            return (
                <div
                    className="suggestion-item"
                    onClick={handleRecommend}
                    style={{ color: notFoundAdded ? 'green' : 'black' }}
                >
                    {msg}
                </div>
            );
        }
        return null;
    };

    const handleRecommend = () => {
        addUserContentRec(query, type);
        setNotFoundAdded(true);
        setQuery('');
        setSuggestions([]);
    };

    const showYear = (suggestion) => {
        return type === 'MEDIA' ? suggestion?.year || '' : suggestion?.date_of_birth?.split('-')[0] || '';
    };

    const showSuggestionsWithImg = () => {
        // limit number of items rendered to avoid rendering overhead
        const MAX_ITEMS = 10;
        return query.length > 0 && suggestions.length > 0 ? (
            <div
                style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', overflowX: 'scroll', gap: 10, padding: 10, cursor:'pointer', }}
            >
                {suggestions
                    .filter(s => !!s.image && !usedIds[type].includes(s.id))
                    .slice(0, MAX_ITEMS)
                    .map((suggestion, index) => (
                        <div
                            key={suggestion.id}
                            onClick={() => handleSuggestionClick(suggestion)}
                            style={{
                                backgroundColor: setGuessStatus(suggestion.id),
                                opacity: 0.8,
                                padding: 10,
                                borderRadius: 10,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 5
                            }}
                        >
                            <img
                                style={{ height: 140 }}
                                src={`https://image.tmdb.org/t/p/w300_and_h450_bestv2/${suggestion.image}`}
                                alt={suggestion.title || suggestion.name}
                            />
                            <p style={{ color: 'white' }}>{suggestion.title || suggestion.name}</p>
                            <p style={{ color: 'white' }}>{showYear(suggestion)}</p>
                        </div>
                    ))}
            </div>
        ) : null;
    };

    const handleFlip = () => {
        flip();
        setQuery('');
        setSuggestions([]);
        focusInput();
    };

    const focusInput = () => {
        searchRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(() => searchRef.current?.focus(), 300); // Delay focus to ensure scrolling completes
    };
    

    return (
        <div className={`search-bar-container ${showNudgeEffect ? 'nudge-effect' : ''} ${showTypeFlash ? 'type-flash' : ''}`}>
            <div
                className={showNudgeEffect ? 'nudge-effect' : ''}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}
            >
                <input
                    ref={searchRef}
                    type="text"
                    value={query}
                    onChange={handleInputChange}
                    className="search-input"
                    placeholder={`Search for ${type !== "MEDIA" ? 'actor or actress' : 'movie'}`}
                    autoComplete='off'
                    inputMode='search'
                />
                <div style={{display:'flex', flexDirection:'row', alignItems:'center', justifyContent:'space-between', gap:20}}>
                    <FontAwesomeIcon
                        style={{ cursor: 'pointer' }}
                        size="lg"
                        icon={['fas', 'lightbulb']}
                        color='#ccc'
                        onClick={triggerHint}
                    />
                    <FontAwesomeIcon
                        style={{ transform: "rotate(90deg)", cursor: 'pointer' }}
                        size="lg"
                        icon={['fas', 'arrow-right-arrow-left']}
                        color='#ccc'
                        onClick={handleFlip}
                    />
                </div>
            </div>
            {loading ? <Loader show={loading} /> : showSuggestionsWithImg()}
            {showNotFoundMsg()}
        </div>
    );
};

export default SearchBar;
